import { useState, useEffect, useRef, type FC } from "react";
import { ArrowRight, Check, PartyPopper } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/utils";
import { supabase } from "@/shared/lib/supabase";
import { usePerson, useUser } from "@/shared/hooks/useAppContext";

import { MODULE_DEFINITIONS, type ModuleStatus } from "@/shared/constants/moduleDefinitions";
import { useModuleProgress } from "@/shared/hooks/useModuleProgress";
import { useUserCustomizations } from "@/shared/hooks/useUserCustomizations";
import { POST_LOSS_ROUTES } from "@/shared/constants/paths";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

const CIRCLE_SIZE = 20;
const STROKE_WIDTH = 2.5;
const RADIUS = (CIRCLE_SIZE - STROKE_WIDTH) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export function createModuleComplete(): FC<FlowCustomComponentProps> {
  const Wrapped: FC<FlowCustomComponentProps> = (props) => <ModuleComplete {...props} />;
  Wrapped.displayName = "ModuleComplete";
  return Wrapped;
}

const ModuleComplete: FC<FlowCustomComponentProps> = ({ onCompleteFlow }) => {
  const navigate = useNavigate();
  const { moduleId: currentModuleId } = useParams<{ moduleId: string }>();
  const person = usePerson();
  const user = useUser();
  const { modules: progressModules } = useModuleProgress();
  const { setCustomization } = useUserCustomizations();

  const allAlreadyCompleted = progressModules.every((m) => m.status === "completed");

  const effectiveAllCompleted =
    allAlreadyCompleted ||
    progressModules.every((m) => m.status === "completed" || m.id === currentModuleId);

  const nextModule = effectiveAllCompleted
    ? null
    : (MODULE_DEFINITIONS.find((def) => {
        if (def.id === currentModuleId) return false;
        const progress = progressModules.find((m) => m.id === def.id);
        return !progress || progress.status !== "completed";
      }) ?? null);

  const nextModuleId = nextModule?.id ?? null;

  const onCompleteFlowRef = useRef(onCompleteFlow);
  onCompleteFlowRef.current = onCompleteFlow;

  if (effectiveAllCompleted && !allAlreadyCompleted) {
    return (
      <InventoryCompleteLoader
        onComplete={async () => {
          if (person?.estate_id && user?.user_id) {
            supabase.functions
              .invoke("communications/inventory-complete-check", {
                body: { estate_id: person.estate_id, user_id: user.user_id },
              })
              .catch((err) => console.warn("inventory-complete-check failed:", err));
          }
          await setCustomization("estate_inventory_onboarding_seen", true);
          onCompleteFlowRef.current?.();
          navigate(POST_LOSS_ROUTES.ESTATE_INVENTORY, { replace: true });
        }}
      />
    );
  }

  const handleNavigate = () => {
    if (effectiveAllCompleted) {
      onCompleteFlow?.();
      navigate(POST_LOSS_ROUTES.ESTATE_INVENTORY, { replace: true });
    } else if (nextModuleId) {
      onCompleteFlow?.();
      navigate(`/${nextModuleId}`, { replace: true });
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-[500px] flex-col gap-6">
      <h2 className="text-center">
        {effectiveAllCompleted
          ? "You\u2019ve completed your estate inventory"
          : nextModule
            ? `Next, let\u2019s add ${nextModule!.title.toLowerCase()}`
            : ""}
      </h2>
      <div className="overflow-hidden rounded-lg border border-primary bg-white">
        {MODULE_DEFINITIONS.map((definition) => {
          const moduleProgress = progressModules.find((m) => m.id === definition.id);
          const isCurrentModule = definition.id === currentModuleId;
          const status: ModuleStatus =
            effectiveAllCompleted || isCurrentModule
              ? "completed"
              : (moduleProgress?.status ?? "not_started");
          const progress =
            effectiveAllCompleted || isCurrentModule ? 1 : (moduleProgress?.progress ?? 0);
          const isNext = !effectiveAllCompleted && definition.id === nextModuleId;

          return (
            <ModuleRow
              key={definition.id}
              title={definition.title}
              description={definition.description}
              status={status}
              progress={progress}
              isNext={isNext}
              onStart={isNext ? handleNavigate : undefined}
            />
          );
        })}
        <div className="flex items-center gap-3 p-6">
          <div className="flex h-5 w-5 shrink-0 items-center justify-center">
            <PartyPopper className="h-4 w-4 text-secondary" />
          </div>
          <p className="text-sm text-secondary">You&apos;re ready for estate settlement</p>
        </div>
      </div>

      <Button variant="brand" rounded={true} onClick={handleNavigate} className="w-full">
        {effectiveAllCompleted ? (
          <>
            View estate inventory <ArrowRight className="h-4 w-4" />
          </>
        ) : (
          <>
            Let&apos;s get started <ArrowRight className="h-4 w-4" />
          </>
        )}
      </Button>
    </div>
  );
};

interface ModuleRowProps {
  title: string;
  description: string;
  status: ModuleStatus;
  progress: number;
  isNext: boolean;
  onStart?: () => void;
}

const ModuleRow: FC<ModuleRowProps> = ({
  title,
  description,
  status,
  progress,
  isNext,
  onStart,
}) => {
  return (
    <div
      className={cn(
        "flex items-center gap-3 border-b border-primary p-6 last:border-b-0 transition-colors",
        isNext && "cursor-pointer hover:bg-subtle",
      )}
      onClick={isNext ? onStart : undefined}
    >
      <ProgressIndicator status={status} progress={progress} />

      <div className="min-w-0 flex-1">
        <p className={cn("text-sm text-secondary", isNext && "font-medium text-primary")}>
          {title}
        </p>
        {isNext && <p className="mt-1 text-sm text-secondary">{description}</p>}
      </div>

      {isNext && onStart && (
        <button
          type="button"
          className="flex shrink-0 items-center gap-1 text-sm font-medium text-brand"
          onClick={(e) => {
            e.stopPropagation();
            onStart();
          }}
        >
          Start <ArrowRight className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};

const ProgressIndicator: FC<{ status: ModuleStatus; progress: number }> = ({
  status,
  progress,
}) => {
  if (status === "completed") {
    return (
      <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand">
        <Check className="h-3 w-3 text-white" strokeWidth={2.5} />
      </div>
    );
  }

  if (status === "in_progress") {
    const offset = CIRCUMFERENCE * (1 - progress);
    return (
      <svg width={CIRCLE_SIZE} height={CIRCLE_SIZE} className="shrink-0 -rotate-90">
        <circle
          cx={CIRCLE_SIZE / 2}
          cy={CIRCLE_SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke="#d6d3d1"
          strokeWidth={STROKE_WIDTH}
        />
        <circle
          cx={CIRCLE_SIZE / 2}
          cy={CIRCLE_SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke="#008251"
          strokeWidth={STROKE_WIDTH}
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
    );
  }

  return <div className="h-5 w-5 shrink-0 rounded-full border-2 border-stone-200" />;
};

const LOADING_MESSAGES = [
  "Organizing financial accounts",
  "Calculating estate value",
  "Identifying next steps",
  "Cross-referencing for completeness",
];

const MESSAGE_INTERVAL_MS = 1500;
const COMPLETE_DELAY_MS = 500;

const InventoryCompleteLoader: FC<{ onComplete: () => void }> = ({ onComplete }) => {
  const [visibleCount, setVisibleCount] = useState(0);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    const interval = setInterval(() => {
      setVisibleCount((prev) => {
        const next = prev + 1;
        if (next >= LOADING_MESSAGES.length) {
          clearInterval(interval);
          setTimeout(() => onCompleteRef.current(), COMPLETE_DELAY_MS);
        }
        return next;
      });
    }, MESSAGE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-subtle">
      <div className="flex flex-col items-center">
        <h2 className="text-center text-[30px] leading-[36px]">
          Building your estate inventory...
        </h2>
        <div className="mt-6 flex flex-col gap-3">
          {LOADING_MESSAGES.map((message, index) => (
            <div
              key={message}
              className={cn(
                "flex items-center gap-2.5",
                index < visibleCount ? "animate-fade-in-down" : "invisible",
              )}
            >
              <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand">
                <Check className="h-3 w-3 text-white" strokeWidth={2.5} />
              </div>
              <p className="text-[16px] text-secondary">{message}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
